import { chain, find, get, isEmpty, some, sortBy } from 'lodash'
import { isHidden, isVisible } from './status'
import { RaceLogicMarkets, RaceMarketsMultilegsTabs } from '../constants'

export function isMarketTypeParametric (marketType) {
  try {
    const firstMarket = Object.values(marketType.markets)[0]

    if (!firstMarket) {
      return false
    }

    // If specifiers not emtry string - than its parametric
    return !isEmpty(firstMarket.specifiers)
  } catch (e) {
    return false
  }
}

/*
  @param {Event} event
  @param {MarketTypeInstruction} displayedMarketTypes

  @return {ExtractedMarketType[]} event extracted market types
*/
export function extractEventMarketTypes (eventMarketTypes, displayedMarketTypes) {
  if (isEmpty(displayedMarketTypes) || isEmpty(eventMarketTypes)) {
    return []
  }

  const extractedMarkets = displayedMarketTypes.reduce(
    (acc, displayedMarketType) => {
      const targetMarket = eventMarketTypes[displayedMarketType.id]

      if (targetMarket) {
        return [
          ...acc,
          {
            ...targetMarket,
            outcomeTypes: displayedMarketType.outcomeTypes,

            periodTypeId: displayedMarketType.periodTypeId,
            periodTypeName: displayedMarketType.periodTypeName,
            parameterized: displayedMarketType.parameterized
          }
        ]
      }

      return [
        ...acc,
        {
          id: displayedMarketType.id,
          name: displayedMarketType.name,
          outcomeTypes: displayedMarketType.outcomes,
          parameterized: false
        }
      ]
    },
    []
  )

  return extractedMarkets
}

const DEFAULT_MARKET_POSITION = 999

export function getMarketSortingFunction () {
  return (a, b) => (
    (a.orderPosition || DEFAULT_MARKET_POSITION) - (b.orderPosition || DEFAULT_MARKET_POSITION)
  )
}

export function isMarketVisible (market) {
  // Market active if self status and outcomes statuses are active
  if (isHidden(market) || isEmpty(market.outcomes)) {
    return false
  }

  for (const outcome of Object.values(market.outcomes)) {
    if (isVisible(outcome)) {
      return true
    }
  }

  return false
}

export function isMarketTypeVisible (marketType) {
  if (isEmpty(marketType.markets)) {
    return false
  }

  // Market type visible when at least one market visible
  for (const market of Object.values(marketType.markets)) {
    if (isMarketVisible(market)) {
      return true
    }
  }

  return false
}

// ft = full time
const ftPeriodTypeId = 1
// ftot = full time + over time
const ftotPeriodTypeId = 18
export function getSportMainMarketTypesByPeriod (mainMarketTypes) {
  const mainMarketTypesByPeriod = chain(mainMarketTypes)
    .map(mainMarketType => ({
      ...mainMarketType,
      // full time and full time + over time periods should be displayed together as full time period
      periodTypeId: mainMarketType.periodTypeId === ftotPeriodTypeId ? ftPeriodTypeId : mainMarketType.periodTypeId
    }))
    .groupBy('periodTypeId')
    .sortBy((period) => {
      return get(period, '[0].mainOrderPosition')
    })
    .map((period) => {
      return sortBy(period, 'mainOrderPosition')
    })
    .value()

  if (isEmpty(mainMarketTypesByPeriod)) {
    return {
      fullPeriod: {
        marketTypes: [],
        periodTypeId: null,
        periodTypeName: null
      },
      subPeriods: []
    }
  }

  const fullPeriodMainMarketTypes = mainMarketTypesByPeriod[0]
  const { periodTypeId: fullPeriodTypeId, periodTypeName: fullPeriodTypeName } = fullPeriodMainMarketTypes[0]

  const fullPeriod = {
    marketTypes: fullPeriodMainMarketTypes,
    periodTypeId: fullPeriodTypeId,
    periodTypeName: fullPeriodTypeName
  }

  const subPeriods = chain(mainMarketTypesByPeriod)
    .slice(1)
    .map((period) => {
      const { periodTypeId: subPeriodTypeId, periodTypeName: subPeriodTypeName } = period[0]

      return {
        marketTypes: period,
        periodTypeId: subPeriodTypeId,
        periodTypeName: subPeriodTypeName
      }
    })
    .value()

  return {
    fullPeriod,
    subPeriods
  }
};

export const getIsMainMarketTypeTotal = (mainMarketType) => chain(mainMarketType)
  .get('groups')
  .includes('TOTALS')
  .value()

export const getMarketsFromPeriod = (period, marketTypes) => {
  const markets = chain(period)
    .get('marketTypes')
    .map((mainMarketType) => {
      const { id: marketTypeId, parameterized, outcomeTypes, providerName } = mainMarketType
      const isTotal = getIsMainMarketTypeTotal(mainMarketType)
      let outcomes
      const market = chain(marketTypes)
        .get(`[${marketTypeId}].markets`)
        .find(({ isMain }) => isMain)
        .value() || {}
      const marketOutcomes = get(market, 'outcomes')
      const isMarketHidden = isHidden(market)

      if (!marketOutcomes || isMarketHidden) {
        outcomes = chain(outcomeTypes)
          .map(({ id, orderPosition }) => ({ id, fakeOutcome: true, orderPosition }))
          .sortBy('orderPosition')
          .value()
      } else {
        outcomes = chain(outcomeTypes)
          .map(({ id, orderPosition }) => {
            const outcome = find(marketOutcomes, { outcomeTypeId: id })

            return (outcome && !isHidden(outcome) && outcome.odds)
              ? outcome
              : { id, fakeOutcome: true, orderPosition }
          })
          .sortBy('orderPosition')
          .value()
      }

      return {
        ...market,
        marketTypeId,
        providerName,
        parameterized,
        isTotal,
        outcomes
      }
    })
    .value()

  return {
    ...period,
    markets
  }
}

export const getEventMainMarketsByPeriod = (mainMarketTypesByPeriod, event) => {
  const { marketTypes } = event
  const { fullPeriod, subPeriods } = mainMarketTypesByPeriod

  const fullPeriodWithMarkets = getMarketsFromPeriod(fullPeriod, marketTypes)
  const subPeriodsWithMarkets = chain(subPeriods)
    .map((period) => getMarketsFromPeriod(period, marketTypes))
    .filter(({ markets }) => some(
      markets,
      ({ outcomes }) => Boolean(
        find(outcomes, ({ fakeOutcome }) => !fakeOutcome)
      )
    ))
    .value()

  return {
    fullPeriod: fullPeriodWithMarkets,
    subPeriods: subPeriodsWithMarkets
  }
}

export const isExoticMarket = (marketType) => [
  RaceLogicMarkets.QUIN,
  RaceLogicMarkets.EXACTA,
  RaceLogicMarkets.TRIFECTA,
  RaceLogicMarkets.FIRST4].includes(marketType)

export const isMultilegsMarket = (marketType) => !!RaceMarketsMultilegsTabs[marketType]
